import React, { useContext as useContextCore } from 'react'
import { telemetryConfig } from '../../config/settings'
import type { Colors, RouterType } from '@ztechapps/common.hook'
import { SSRProvider } from 'react-bootstrap'
import { ThemeProvider, ToastProvider } from '@ztechapps/webui.util'
import { ConfirmDialogProvider } from '@ztechapps/webui.modal'
import { DependencyProvider } from '@ztechapps/webui.contract'
import { TelemetryProvider, useWebVitals, usePageView } from '@ztechapps/webui.telemetry'
import { setupContainer } from '../../config/setup-container'
import { PRIMARY_BRAND_COLOR } from '../../config/theme'

// The 75/50/25 suffixes are white-tint percentages (primary75 lightest, primary25 darkest);
// each is PRIMARY_BRAND_COLOR composited over white at the complementary opacity (25/50/75%).
const themeColors: Colors = {
  primary: PRIMARY_BRAND_COLOR,
  primary75: '#c4e5f9',
  primary50: '#88cbf4',
  primary25: '#4db1ee',
}

export type ContextType = {
  routerType?: RouterType
}

export type ProviderProps = ContextType & {
  children: React.ReactNode
}

const context = React.createContext<ContextType>({} as unknown as ContextType)
const CoreProvider = context.Provider

//we need to load all the modules here
const container = setupContainer()

/**
 * Inner component that initializes telemetry
 * Must be inside TelemetryProvider
 */
function TelemetryInitializer({ children }: { children: React.ReactNode }) {
  // Collect Core Web Vitals
  useWebVitals()
  // Track page views on route changes (OTel)
  usePageView()

  return (
    <CoreProvider
      value={{
        routerType: 'react-router',
      }}
    >
      {children}
    </CoreProvider>
  )
}

export const AppContextProvider = ({ children }: ProviderProps) => {
  const routerType: RouterType = 'react-router'

  return (
    <TelemetryProvider config={telemetryConfig}>
      <DependencyProvider container={container}>
        <SSRProvider>
          <ThemeProvider routerType={routerType} iconType={'fai'} colors={themeColors}>
            <ToastProvider>
              <TelemetryInitializer>
                <ConfirmDialogProvider>{children}</ConfirmDialogProvider>
              </TelemetryInitializer>
            </ToastProvider>
          </ThemeProvider>
        </SSRProvider>
      </DependencyProvider>
    </TelemetryProvider>
  )
}

export const useAppContext = () => useContextCore(context)
